import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, ChevronDown } from 'lucide-react';
import { coworkForestaApi } from '../../lib/api';
import { buildTrimestres, currentCycleKey } from './CoworkLiquidacionTrimestral';

const fmt = (n) => {
  const v = Number(n) || 0;
  const s = Math.abs(v).toLocaleString('es-AR', { maximumFractionDigits: 0 });
  return `${v < 0 ? '-' : ''}$${s}`;
};

const EGRESO_ROWS = [
  ['iibb', 'IIBB'],
  ['expensas', 'Expensas'],
  ['edemsa', 'Edemsa'],
  ['municipalidad_agua', 'Municipalidad y Agua'],
  ['impuesto_inmobiliario', 'Impuesto inmobiliario'],
  ['servicio_limpieza', 'Servicio de limpieza'],
  ['marketing_pauta', 'Marketing y pauta'],
  ['puesta_punto_cowork', 'Legales/contables'],
  ['mantenimiento_general', 'Mantenimiento general'],
];

const SectionBand = ({ children }) => (
  <div className="bg-[#2f5e4e] text-white text-sm font-bold uppercase tracking-wider px-4 py-2">{children}</div>
);

const Line = ({ label, value, bold = false, indent = false }) => (
  <div className="flex items-center justify-between px-4 py-1.5 border-b border-slate-50">
    <span className={`text-sm text-slate-700 ${bold ? 'font-bold' : ''} ${indent ? 'pl-0' : ''}`}>{label}</span>
    <span className={`text-sm font-mono tabular-nums ${bold ? 'font-bold text-slate-900' : 'text-slate-700'}`}>{value}</span>
  </div>
);

export default function CoworkVistaInversor({ edificioId }) {
  const trimestres = useMemo(buildTrimestres, []);
  const [trimestreKey, setTrimestreKey] = useState(currentCycleKey);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [invId, setInvId] = useState('');

  useEffect(() => {
    if (!edificioId || !trimestreKey) return;
    setLoading(true);
    coworkForestaApi.rendicionInversores(edificioId, trimestreKey)
      .then((r) => {
        setData(r.data);
        const inv = r.data?.inversores || [];
        setInvId((prev) => (inv.find((i) => i.propietario_id === prev) ? prev : (inv[0]?.propietario_id || '')));
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [edificioId, trimestreKey]);

  const liq = data?.liquidacion;
  const comp = liq?.computados;
  const filas = liq?.filas || {};
  const inversor = (data?.inversores || []).find((i) => i.propietario_id === invId);

  return (
    <div className="max-w-3xl mx-auto" data-testid="cowork-vista-inversor">
      {/* Selector de trimestre (control del reporte) */}
      <div className="flex flex-wrap items-center gap-4 mb-4">
        <select
          value={trimestreKey}
          onChange={(e) => setTrimestreKey(e.target.value)}
          className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
          data-testid="vi-trimestre-selector"
        >
          {trimestres.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
        <span className="text-sm font-medium text-slate-700" data-testid="vi-ocupacion">
          Ocupación: {data?.ocupacion_pct || 0}%
        </span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64 text-slate-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando…</div>
      ) : (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          {/* Resumen general del trimestre */}
          <SectionBand>Resumen general del trimestre</SectionBand>
          <div data-testid="vi-resumen">
            <Line label="Ingresos por membresías" value={fmt(filas.ingresos_membresias?.trimestre)} bold />
            <div className="px-4 py-1.5 border-b border-slate-50"><span className="text-sm font-bold text-slate-700">Egresos operativos:</span></div>
            {EGRESO_ROWS.map(([k, lbl]) => <Line key={k} label={lbl} value={fmt(filas[k]?.trimestre)} />)}
            <Line label="Total egresos operativos" value={fmt(comp?.total_egresos_operativos?.trimestre)} bold />
            <Line label="Gastos extraordinarios" value={fmt(filas.gastos_extraordinarios?.trimestre)} />
            <Line label={`Fee de gestión GCSAS (${liq?.fee_pct || 8}%)`} value={fmt(comp?.fee_gestion?.trimestre)} />
            <Line label="Recupero expensas usuarios" value={fmt(filas.recupero_expensas?.trimestre)} />
            <div className="flex items-center justify-between px-4 py-2.5 bg-emerald-50">
              <span className="text-sm font-bold text-emerald-800 uppercase">Resultado neto a distribuir</span>
              <span className="text-base font-bold font-mono tabular-nums text-emerald-800" data-testid="vi-neto">{fmt(comp?.resultado_neto_distribuir?.trimestre)}</span>
            </div>
          </div>

          {/* Detalle del propietario */}
          <div className="mt-4"><SectionBand>Detalle del propietario</SectionBand></div>
          {(data?.inversores || []).length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-slate-400" data-testid="vi-sin-inversores">No hay inversores con puestos en administración en este pool.</div>
          ) : (
            <div>
              <div className="flex items-center justify-between px-4 py-2 border-b border-slate-100">
                <span className="text-sm font-bold text-slate-700">Propietario:</span>
                <div className="relative">
                  <select
                    value={invId}
                    onChange={(e) => setInvId(e.target.value)}
                    className="appearance-none bg-transparent text-sm font-bold text-slate-900 pr-6 text-right focus:outline-none cursor-pointer"
                    data-testid="vi-inversor-selector"
                  >
                    {data.inversores.map((i) => <option key={i.propietario_id} value={i.propietario_id}>{i.nombre}</option>)}
                  </select>
                  <ChevronDown className="w-4 h-4 text-slate-500 absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
              </div>
              <Line label="Puestos" value={inversor?.puestos ?? 0} />
              <div className="flex items-center justify-between px-4 py-1.5 border-b border-slate-50">
                <span className="text-sm text-slate-700">Participación:</span>
                <span className="text-sm font-mono tabular-nums text-slate-700" data-testid="vi-participacion">
                  {(inversor?.participacion_pct ?? 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%
                </span>
              </div>
              <div className="flex items-center justify-between px-4 py-2.5 bg-slate-100">
                <span className="text-sm font-bold text-slate-800">Monto del trimestre:</span>
                <span className="text-base font-bold font-mono tabular-nums text-slate-900" data-testid="vi-monto">{fmt(inversor?.monto_trimestre)}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
